/**
 * This source code is quoted from rc-slider.
 * homepage: https://github.com/react-component/slider
 */
import classNames from 'classnames';
// import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useLayoutEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import warning from 'warning';
import { getNid, prefix } from "../../../wui-core/src/index";
import addEventListener from 'rc-util/es/Dom/addEventListener';
import Icon from '../../../wui-icon/src';
import Handle from '../Handle';
import * as utils from '../utils';
import { useThrottleFn } from '../utils/useThrottle';

import type { AddEventListenerType, FieldidClearProps, GenericSlider, GenericSliderProps, GenericSliderState, HandleProps } from '../iSlider';
import Marks from './Marks';
import Steps from './Steps';

function noop() {
}

/* export const sliderPropTypes = {
    min: PropTypes.number,
    max: PropTypes.number,
    step: PropTypes.number,
    marks: PropTypes.object,
    included: PropTypes.bool,
    className: PropTypes.string,
    prefixCls: PropTypes.string,
    disabled: PropTypes.bool,
    children: PropTypes.any,
    onBeforeChange: PropTypes.func,
    onChange: PropTypes.func,
    onAfterChange: PropTypes.func,
    handle: PropTypes.func,
    dots: PropTypes.bool,
    vertical: PropTypes.bool,
    reverse: PropTypes.bool,
    allowClear: PropTypes.bool,
    style: PropTypes.object,
    handleStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]),
    trackStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]),
    // minimumTrackStyle: PropTypes.object, // just for compatibility, will be deperecate
    // maximumTrackStyle: PropTypes.object, // just for compatibility, will be deperecate
    railStyle: PropTypes.object,
    dotStyle: PropTypes.object,
    activeDotStyle: PropTypes.object,
    tooltipVisible: PropTypes.bool,
    tooltipPlacement: PropTypes.string,
    getTooltipPopupContainer: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
} */


export default function createSlider<
Props extends GenericSliderProps,
State extends GenericSliderState
>(Component: GenericSlider<Props, State>): React.ComponentClass<Props, State> {
    // 创建一个函数式组件增强器
    const ComponentEnhancer = forwardRef<any, Props>((props, ref) => {
        // 创建内部组件引用
        const innerComponentRef = useRef<any>(null);
        const [componentState, setComponentState] = useState<State>({} as State);

        // 用于存储各种引用
        const handlesRefsRef = useRef<{[key: string]: HTMLDivElement}>({});
        const sliderRef = useRef<HTMLDivElement | null>(null);
        const dragOffsetRef = useRef<number>(0);
        const sliderResizeObserverRef = useRef<ResizeObserver | null>(null);

        // 事件监听器引用
        const onTouchMoveListenerRef = useRef<AddEventListenerType | null>(null);
        const onTouchUpListenerRef = useRef<AddEventListenerType | null>(null);
        const onMouseMoveListenerRef = useRef<AddEventListenerType | null>(null);
        const onMouseUpListenerRef = useRef<AddEventListenerType | null>(null);
        const onMouseLeaveListenerRef = useRef<AddEventListenerType | null>(null);

        // 组合的 componentRef，包含内部组件的方法和 createSlider 的方法
        const componentRef = useRef<any>(null);

        // 暴露实例方法给父组件
        useImperativeHandle(ref, () => componentRef.current!, [componentRef.current]);

        // 开发环境下的警告检查
        useEffect(() => {
            // @ts-ignore Node环境变量
            if (process.env.NODE_ENV !== 'production') {
                const {step, max, min} = props;
                warning(
                    step && Math.floor(step) === step ? (max - min) % step === 0 : true,
                    'Slider[max] - Slider[min] (%s) should be a multiple of Slider[step] (%s)',
                    max - min,
                    step
                );
            }
        }, [props.step, props.max, props.min]);

        // getZoom 函数
        const getZoom = useCallback(() => {
            let winodwThis;
            try {
                winodwThis = window; //  兼容 window globalThis（window 兼容IE）
            } catch (error) {
                winodwThis = globalThis;
            }
            if ((winodwThis as any).jDiwork) {
                (winodwThis as any).jDiwork.getContext((data: any) => {
                    setComponentState(prevState => ({
                        ...prevState,
                        zoom: data._SCALE
                    }));
                    if (componentRef.current) {
                        Object.assign(componentRef.current, { state: { ...componentRef.current.state, zoom: data._SCALE } });
                    }
                })
            }
        }, []);
        // 为 ResizeObserver 添加节流处理，使用 @tinper/hooks 的 useThrottleFn
        const { run: throttledGetZoom } = useThrottleFn(getZoom, { wait: 100 });

        // componentDidMount 效果
        useEffect(() => {
            if (sliderRef.current) {
                sliderResizeObserverRef.current = new ResizeObserver(() => {
                    throttledGetZoom();
                });
                sliderResizeObserverRef.current?.observe(sliderRef.current);
            }

            // componentWillUnmount 清理
            return () => {
                if (innerComponentRef.current && innerComponentRef.current.componentWillUnmount) {
                    innerComponentRef.current.componentWillUnmount();
                }
                removeDocumentEvents();
                if (sliderResizeObserverRef.current) {
                    sliderResizeObserverRef.current.disconnect();
                }
                // 清理 DOM 缓存
                utils.clearDomCache();
            };
        }, []);

        // 更新组件状态方法
        const updateComponentState = useCallback((newState: any) => {
            if (typeof newState === 'function') {
                setComponentState(prevState => {
                    const nextState = newState(prevState);
                    return { ...prevState, ...nextState };
                });
            } else {
                setComponentState(prevState => {
                    return { ...prevState, ...newState };
                });
            }
        }, []);

        // removeDocumentEvents 函数
        const removeDocumentEvents = useCallback(() => {
            /* eslint-disable no-unused-expressions */
            onTouchMoveListenerRef.current && onTouchMoveListenerRef.current.remove();
            onTouchUpListenerRef.current && onTouchUpListenerRef.current.remove();
            onMouseMoveListenerRef.current && onMouseMoveListenerRef.current.remove();
            onMouseUpListenerRef.current && onMouseUpListenerRef.current.remove();
            onMouseLeaveListenerRef.current && onMouseLeaveListenerRef.current.remove();
            /* eslint-enable no-unused-expressions */
        }, []);

        // leave 函数
        const leave = useCallback((_e: React.MouseEvent) => {
            if (innerComponentRef.current) {
                innerComponentRef.current.onEnd();
            }
        }, []);

        // 优化：使用 requestAnimationFrame + 简单的forceUpdate触发重新渲染
        // 不再更新tracks/handles的state，而是通过一个dummy state触发重新渲染
        const rafRef = useRef<number | null>(null);
        const [, forceUpdate] = useState(0);
        const triggerUpdate = useCallback(() => {
            forceUpdate(n => n + 1);
        }, []);

        // onMouseMove 函数
        const onMouseMove = useCallback((e: MouseEvent | React.MouseEvent<HTMLDivElement>) => {
            if (!sliderRef.current || !innerComponentRef.current) {
                innerComponentRef.current?.onEnd();
                return;
            }
            const position = utils.getMousePosition(props.vertical, e as any, componentState.zoom as number);
            innerComponentRef.current.onMove(e as any, position - dragOffsetRef.current);
            // 使用 requestAnimationFrame 优化更新
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }
            rafRef.current = requestAnimationFrame(triggerUpdate);
        }, [props.vertical, componentState.zoom, triggerUpdate]);

        // onTouchMove 函数
        const onTouchMove = useCallback((e: TouchEvent | React.TouchEvent<HTMLDivElement>) => {
            // 1.isNotTouchEvent：是不是touch事件
            // 2.getTouchPosition：return vertical ? e.touches[0].clientY : e.touches[0].pageX;
            if (utils.isNotTouchEvent(e as any) || !sliderRef.current || !innerComponentRef.current) {
                innerComponentRef.current?.onEnd();
                return;
            }
            const position = utils.getTouchPosition(props.vertical, e as any);
            innerComponentRef.current.onMove(e as any, position - dragOffsetRef.current);
            // 使用 requestAnimationFrame 优化更新
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }
            rafRef.current = requestAnimationFrame(triggerUpdate);
        }, [props.vertical, triggerUpdate]);

        // addDocumentTouchEvents 函数
        const addDocumentTouchEvents = useCallback(() => {
            // just work for Chrome iOS Safari and Android Browser
            onTouchMoveListenerRef.current = addEventListener(document, 'touchmove', (e: TouchEvent) => {
                onTouchMove(e);
            });
            onTouchUpListenerRef.current = addEventListener(document, 'touchend', () => innerComponentRef.current?.onEnd());
        }, [onTouchMove]);

        // addDocumentMouseEvents 函数
        const addDocumentMouseEvents = useCallback(() => {
            // Use native MouseEvent for document event listeners
            onMouseMoveListenerRef.current = addEventListener(document, 'mousemove', (e: MouseEvent) => {
                onMouseMove(e);
            });
            onMouseUpListenerRef.current = addEventListener(document, 'mouseup', (_e: MouseEvent) => {
                innerComponentRef.current?.onEnd();
            });
            onMouseLeaveListenerRef.current = addEventListener(document, 'mouseleave', (e: MouseEvent) => {
                leave(e as any);
            }); // iframe嵌套的情况，脱离iframe后onmousedown依然生效bug修复（更多设置-->图片设置）
        }, [onMouseMove, leave]);

        // onMouseDown 函数
        const onMouseDown = useCallback((e: React.MouseEvent) => {
            // 鼠标落下的操作
            // 指示当事件被触发时哪个鼠标按键被点击
            // 参数  描述
            // 0     规定鼠标左键。
            // 1     规定鼠标中键。
            // 2     规定鼠标右键。
            if (e.button !== 0) {
                return;
            }
            // 1.getMousePosition：获得鼠标的位置 clientY或者pageX
            // 2.isEventFromHandle：是直接落下还是拖动handle
            // 3.getHandleCenterPosition:获得handle的中点位置
            // 4.onStart：在slider.jsx中
            const isVertical = props.vertical;
            let position = utils.getMousePosition(isVertical, e, componentState.zoom as number);
            if (!utils.isEventFromHandle(e, handlesRefsRef.current)) {
                dragOffsetRef.current = 0;
            } else {
                const handlePosition = utils.getHandleCenterPosition(isVertical, e.target as HTMLElement);// handle的位置
                dragOffsetRef.current = position - handlePosition;// 鼠标落下的位置-handle的中间位置，这是偏差
                position = handlePosition;// position是
            }
            innerComponentRef.current?.onStart(position);
            addDocumentMouseEvents();
            utils.pauseEvent(e);
            // 优化：在开始拖动时触发重新渲染
            requestAnimationFrame(triggerUpdate);
        }, [props.vertical, componentState.zoom, addDocumentMouseEvents, triggerUpdate]);

        // onTouchStart 函数
        const onTouchStart = useCallback((e: React.TouchEvent) => {
            // touch事件
            // event.touches，多点触碰时的位置数组，比如缩放手势必须要用两指的触摸点，就是一个数组
            // 多点触碰不算
            if (utils.isNotTouchEvent(e)) return;
            // 1.getMousePosition：获得鼠标的位置 clientY或者pageX
            // 2.isEventFromHandle：是直接落下还是拖动handle
            // 3.getHandleCenterPosition:获得handle的中点位置
            // 4.onStart：在slider.jsx中
            const isVertical = props.vertical;
            let position = utils.getTouchPosition(isVertical, e);
            if (!utils.isEventFromHandle(e, handlesRefsRef.current)) {
                dragOffsetRef.current = 0;
            } else {
                const handlePosition = utils.getHandleCenterPosition(isVertical, e.target as HTMLElement);
                dragOffsetRef.current = position - handlePosition;
                position = handlePosition;
            }
            innerComponentRef.current?.onStart(position);
            addDocumentTouchEvents();
            utils.pauseEvent(e);
            // 优化：在开始拖动时触发重新渲染
            requestAnimationFrame(triggerUpdate);
        }, [props.vertical, addDocumentTouchEvents, triggerUpdate]);

        // onKeyDown 函数
        const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
            // 对应不同的keycode
            if (sliderRef.current && utils.isEventFromHandle(e, handlesRefsRef.current)) {
                innerComponentRef.current?.onKeyboard(e);
            }
        }, []);

        // onFocus 函数
        const onFocus = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
            // Focus事件
            // 1.isEventFromHandle：是直接落下还是拖动handle
            // 2.getHandleCenterPosition:获得handle的中点位置
            // 3.onStart：在slider.jsx中
            const isVertical = props.vertical;

            if (utils.isEventFromHandle(e, handlesRefsRef.current)) {
                const handlePosition = utils.getHandleCenterPosition(isVertical, e.target);

                dragOffsetRef.current = 0;
                innerComponentRef.current?.onStart(handlePosition);
                utils.pauseEvent(e);
            }
        }, [props.vertical]);

        // onBlur 函数
        const onBlur = useCallback((_e: React.FocusEvent<HTMLDivElement>) => {
            innerComponentRef.current?.onEnd();
        }, []);

        // getSliderStart 函数
        const getSliderStart = useCallback(() => {
            const slider = sliderRef.current as HTMLDivElement;
            const rect = slider.getBoundingClientRect();
            const {dir: direction} = props;
            return props.vertical ? (props.reverse ? rect.bottom : rect.top) : (props.reverse ? (direction === "rtl" ? rect.left : rect.right) : (direction === "rtl" ? rect.right : rect.left));
        }, [props.vertical, props.reverse, props.dir]);

        // getSliderLength 函数
        const getSliderLength = useCallback(() => {
            const slider = sliderRef.current;
            if (!slider) {
                return 0;
            }

            const coords = slider.getBoundingClientRect();
            return props.vertical ? coords.height : coords.width;
        }, [props.vertical]);

        // calcValue 函数
        const calcValue = useCallback((offset: number) => {
            const {vertical, min, max} = props;
            const ratio = Math.abs(Math.max(offset, 0) / getSliderLength());
            const value = vertical ? (1 - ratio) * (max - min) + min : ratio * (max - min) + min;
            return value;
        }, [props.vertical, props.min, props.max, getSliderLength]);

        // calcValueByPos 函数
        const calcValueByPos = useCallback((position: number) => {
            // mouseposition 或者 handlePosition - slider的top（或者left）；
            const pixelOffset = props.reverse ? getSliderStart() - position : position - getSliderStart();
            // calValue()：radio来算出真正的value
            // trimAlignValue()：在slider.jsx中1.ensureValueInRange 先算出是否可取 v 或者 min 或者 max 2.ensureValuePrecision：来获取真的前进数值closest数值
            const nextValue = innerComponentRef.current?.trimAlignValue(calcValue(props.dir === 'rtl' ? -pixelOffset : pixelOffset));
            return nextValue;
        }, [props.reverse, props.dir, getSliderStart, calcValue]);

        // calcOffset 函数
        const calcOffset = useCallback((value: number) => {
            const {min, max} = props;
            const ratio = (value - min) / (max - min);
            return ratio * 100;
        }, [props.min, props.max]);

        // saveSlider 函数
        const saveSlider = useCallback((slider: HTMLDivElement) => {
            sliderRef.current = slider;
        }, []);

        // saveHandle 函数
        const saveHandle = useCallback((index: number, handle: HTMLDivElement) => {
            if (handle) {
                handlesRefsRef.current[index] = handle;
            }
        }, []);

        // clearValue 函数
        const clearValue = useCallback((_e: React.MouseEvent<HTMLDivElement>) => {
            const {disabled} = props;
            if (disabled) return;
            innerComponentRef.current?.clearSliderValue?.();
            // 使用 Promise 延迟触发重新渲染，等待 setValue 异步更新完成
            Promise.resolve().then(() => {
                triggerUpdate();
            });
        }, [props.disabled, triggerUpdate]);

        // 优化：使用useMemo缓存方法对象，减少不必要的重新创建
        // 只在innerComponentRef或关键方法变化时才重新创建
        const componentMethods = React.useMemo(() => ({
            getSliderStart,
            getSliderLength,
            calcValue,
            calcValueByPos,
            calcOffset,
            saveHandle,
        }), [getSliderStart, getSliderLength, calcValue, calcValueByPos, calcOffset, saveHandle]);

        // 组合所有方法到 componentRef，但减少依赖项
        useEffect(() => {
            if (innerComponentRef.current) {
                // 只在必要时更新componentRef
                componentRef.current = {
                    ...innerComponentRef.current,
                    ...componentMethods,
                    state: componentState,
                    setState: updateComponentState
                };
            }
        }, [innerComponentRef.current, componentMethods, componentState, updateComponentState]);

        // 优化：首次渲染完成后触发更新，确保tracks/handles被渲染
        const isFirstRenderComplete = useRef(false);

        useLayoutEffect(() => {
            if (innerComponentRef.current) {
                const timer = setTimeout(() => {
                    const tracks = innerComponentRef.current?.tracks;
                    const handles = innerComponentRef.current?.handles;
                    // 检查是否有有效内容
                    const hasValidContent = (tracks !== null && tracks !== undefined) ||
                                            (handles !== null && handles !== undefined);
                    if (hasValidContent) {
                        isFirstRenderComplete.current = true;
                        triggerUpdate();
                    }
                }, 0);

                return () => clearTimeout(timer);
            }
        }, []);

        // 优化：监听props.value变化并更新componentState
        useEffect(() => {
            if (props.value !== undefined && props.value !== componentState.value) {
                setComponentState(prevState => ({
                    ...prevState,
                    value: props.value
                }));
            }
        }, [props.value]);

        // 优化：props变化时也触发更新，确保tracks/handles同步
        useEffect(() => {
            if (isFirstRenderComplete.current) {
                triggerUpdate();
            }
        }, [props.value, props.defaultValue, props.reverse, triggerUpdate]);

        // 清理 RAF
        useEffect(() => {
            return () => {
                if (rafRef.current) {
                    cancelAnimationFrame(rafRef.current);
                }
            };
        }, []);

        // render 函数
        const renderContent = () => {
            const {
                prefixCls,
                className,
                marks,
                dots,
                step,
                included,
                disabled,
                vertical,
                reverse,
                min,
                max,
                children,
                // maximumTrackStyle,
                style,
                railStyle,
                dotStyle,
                allowClear,
                range,
                activeDotStyle,
                dir: direction,
                fieldid,
            } = props;

            const showClear = !!allowClear && !range;

            const sliderClassName = classNames(prefixCls, {
                [`${prefixCls}-with-marks`]: Object.keys(marks).length,
                [`${prefixCls}-disabled`]: disabled,
                [`${prefixCls}-vertical`]: vertical,
                [`${prefixCls}-clear`]: showClear,
                [className]: className,
            });

            const sliderWrapperClassName = classNames(
                `${prefixCls}-wrapper`,
                {
                    [`${prefixCls}-wrapper-vertical`]: vertical
                }
            )

            const sliderIconClassName = classNames(
                `${prefixCls}-clear-icon`,
                {
                    [`${prefixCls}-clear-disabled`]: disabled,
                    [`${prefixCls}-clear-vertical`]: vertical
                }
            )
            const fieldidObj:FieldidClearProps = {}
            if (fieldid || fieldid === 0) {
                fieldidObj.rail = `${fieldid}_slider_rail`
                fieldidObj.clear = `${fieldid}_slider_close`
            }
            let adapterNid = getNid(props)
            // 优化：直接从 innerComponentRef 读取 tracks 和 handles，不使用state
            // 这避免了额外的渲染周期，提升初始渲染和批量渲染性能
            const tracks = innerComponentRef.current?.tracks || null;
            const handles = innerComponentRef.current?.handles || null;

            const sliderDom = (
                <div
                    ref={saveSlider}
                    className={sliderClassName}
                    onTouchStart={disabled ? noop : onTouchStart}
                    onMouseDown={disabled ? noop : onMouseDown}
                    onKeyDown={disabled ? noop : onKeyDown}
                    onFocus={disabled ? noop : onFocus}
                    onBlur={disabled ? noop : onBlur}
                    style={style}
                    fieldid={fieldid as string}
                    {...adapterNid}
                >
                    <div
                        className={`${prefixCls}-rail`}
                        fieldid={fieldidObj.rail}
                        style={{
                            // ...maximumTrackStyle,
                            ...railStyle,
                        }}
                    />
                    {tracks}
                    <Steps
                        prefixCls={prefixCls}
                        vertical={vertical}
                        reverse={reverse}
                        marks={marks}
                        dots={dots}
                        step={step!}
                        included={included}
                        lowerBound={innerComponentRef.current?.getLowerBound?.() || min}
                        upperBound={innerComponentRef.current?.getUpperBound?.() || max}
                        max={max}
                        min={min}
                        dir={direction}
                        dotStyle={dotStyle}
                        activeDotStyle={activeDotStyle}
                        fieldid={fieldid}
                    />
                    {handles}
                    <Marks
                        className={`${prefixCls}-mark`}
                        vertical={vertical}
                        reverse={reverse}
                        marks={marks}
                        dir={direction}

                        included={included}
                        lowerBound={innerComponentRef.current?.getLowerBound?.() || min}
                        upperBound={innerComponentRef.current?.getUpperBound?.() || max}
                        max={max}
                        min={min}
                        fieldid={fieldid}
                    />
                    {children}
                </div>
            )

            if (showClear) {
                return (<div className={sliderWrapperClassName} {...adapterNid}>
                    {sliderDom}
                    <div className={sliderIconClassName} fieldid={fieldidObj.clear} onClick={e => clearValue(e)}><Icon type="uf-close-c"/>
                    </div>
                </div>)
            } else {
                return sliderDom
            }
        };

        // 使用 JSX 创建组件元素
        // Component 是一个类组件构造函数
        const componentProps = {
            ...props,
            calcValueByPos,
            calcOffset,
            saveHandle,
            removeDocumentEvents,
            getSliderStart,
            getSliderLength,
            calcValue,
            state: componentState,
            setState: updateComponentState
        } as Props;

        const componentElement = (
            <Component
                {...componentProps}
                ref={innerComponentRef}
            />
        );

        // 渲染组件和内容
        return (
            <>
                {componentElement}
                {renderContent()}
            </>
        );
    });

    // 设置静态属性
    ComponentEnhancer.displayName = `ComponentEnhancer(${Component.displayName})`;

    // 创建一个包装类来兼容 Class 组件的使用方式
    class ComponentEnhancerWrapper extends React.Component<Props, State> {
        static displayName = ComponentEnhancer.displayName;
        static defaultProps = {
            ...Component.defaultProps!,
            prefixCls: `${prefix}-slider`,
            className: '',
            min: 0,
            max: 100,
            step: 1,
            marks: {},
            handle({index, ...restProps}: HandleProps) {
                delete restProps.dragging;
                return <Handle {...restProps} key={index}/>;
            },
            onBeforeChange: noop,
            onChange: noop,
            onAfterChange: noop,
            included: true,
            disabled: false,
            dots: false,
            vertical: false,
            reverse: false,
            allowClear: false,
            trackStyle: [{}],
            handleStyle: [{}],
            railStyle: {},
            dotStyle: {},
            activeDotStyle: {},
        };

        private ref = React.createRef<any>();

        render() {
            // 使用展开运算符传递 props 和 ref
            const props = this.props as any;
            return <ComponentEnhancer {...props} ref={this.ref} />;
        }
    }

    return ComponentEnhancerWrapper as any;
}